import type { ChartData, ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  categoryAxis,
  chartSummary,
  legendOptions,
  tooltipOptions,
  valueAxis,
} from '../charts/chart-options.ts';
import { ChartFrame, hasChartData } from '../charts/chart-frame.tsx';
import { seriesColor, type ChartBaseProps } from '../charts/palette.ts';
import { registerCharts } from '../charts/register.ts';
import { useThemeColors } from '../charts/useThemeColors.ts';

registerCharts();

export type LineChartProps = ChartBaseProps;

/**
 * A trend over time: one line per series, one x position per label.
 *
 * Points are hidden until hovered. Thirty days of daily metrics is thirty dots
 * on every line, and the shape — which is the only reason to draw a line chart
 * rather than print the numbers — disappears behind them.
 */
export function LineChart({
  series,
  labels,
  height = 220,
  yFormat,
  xFormat,
  showLegend,
  ariaLabel,
  emptyTitle = 'No data yet',
  className,
}: LineChartProps) {
  const theme = useThemeColors();

  const data: ChartData<'line'> = {
    labels: labels.map((label, index) => (xFormat ? xFormat(label, index) : label)),
    datasets: series.map((one, index) => {
      const color = seriesColor(theme.series, one, index);
      return {
        label: one.label,
        data: one.data,
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        borderWidth: 2,
        tension: 0.3,
        /* A series of one has no line to be, so it stays a visible dot. */
        pointRadius: one.data.length > 1 ? 0 : 3.5,
        pointHoverRadius: 4,
        pointHitRadius: 12,
      };
    }),
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: legendOptions(theme, showLegend ?? series.length > 1),
      tooltip: tooltipOptions(theme, yFormat),
    },
    scales: { x: categoryAxis(theme), y: valueAxis(theme, yFormat) },
  };

  return (
    <ChartFrame
      height={height}
      ariaLabel={ariaLabel}
      hasData={hasChartData(series)}
      emptyTitle={emptyTitle}
      summary={chartSummary(ariaLabel, series, labels, yFormat)}
      className={className}
    >
      <Line data={data} options={options} aria-hidden='true' />
    </ChartFrame>
  );
}
